import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Filter } from "lucide-react";
import { SERVICES, SERVICE_CATEGORIES } from "../data/services";

const Services = () => {
  const [active, setActive] = useState("All");
  const filtered = useMemo(() => {
    if (active === "All") return SERVICES;
    return SERVICES.filter((s) => s.category === active);
  }, [active]);

  const cats = ["All", ...SERVICE_CATEGORIES.map((c) => c.key)];

  return (
    <main data-testid="services-page">
      {/* HERO */}
      <section className="bg-white border-b border-[#E5E7EB]" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8 nnc-fade-up">
              <div className="flex items-center gap-3 mb-5">
                <span className="nnc-gold-rule" />
                <span className="nnc-overline">Service Directory</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight nnc-text leading-[1.05]">
                Our Services.
              </h1>
              <p className="mt-8 text-lg leading-relaxed nnc-text max-w-3xl">
                NNC delivers a full continuum of professional services across
                nonprofit foundations, strategy, funding, public sector
                programs, and enterprise advisory. Each service line is led by
                senior practitioners and built to federal-grade standards.
              </p>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <div className="inline-block border border-[#00205B] px-5 py-4">
                <div className="nnc-overline">Practice Areas</div>
                <div className="mt-1 text-4xl font-bold nnc-text">{SERVICES.length}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GRANTS vs CONTRACTS EXPLAINER */}
      <section className="bg-white" data-testid="services-grants-vs-contracts">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-12 pb-4">
          <div className="flex items-center gap-3 mb-4">
            <span className="nnc-gold-rule" />
            <span className="nnc-overline">Two Distinct Lanes</span>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight nnc-text max-w-4xl">
            Grants for nonprofits. Contracts for government contractors. <br />
            <span className="nnc-gold-text">We work both lanes — and we don&apos;t confuse them.</span>
          </h2>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-px bg-[#E5E7EB] border border-[#00205B]">
            {/* GRANTS LANE */}
            <div className="bg-white p-8 lg:p-10" data-testid="lane-grants">
              <div className="flex items-center justify-between mb-4">
                <span className="nnc-overline">Lane 01</span>
                <span className="text-xs font-bold uppercase tracking-[0.2em] nnc-text border border-[#00205B] px-3 py-1">
                  Financial Assistance
                </span>
              </div>
              <h3 className="text-xl lg:text-2xl font-bold nnc-text">
                Grants &amp; Cooperative Agreements
              </h3>
              <p className="mt-3 text-sm leading-relaxed nnc-text">
                Federal financial assistance to nonprofits, public agencies,
                tribes, and educational institutions. Governed by 2 CFR 200
                (Uniform Guidance). Mission-driven funding for programs and
                public benefit.
              </p>
              <ul className="mt-5 space-y-2 text-sm nnc-text">
                {[
                  "Grant research, writing, and submission",
                  "Pre-award and post-award administration",
                  "Federal compliance (2 CFR 200, Single Audit)",
                  "Periodic reporting and recordkeeping",
                  "Sub-recipient monitoring",
                ].map((x, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="mt-2 inline-block w-2 h-2 nnc-gold-bg shrink-0" />
                    <span>{x}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 flex flex-wrap gap-2">
                <Link
                  to="/services/grant-research-writing"
                  className="text-xs font-bold uppercase tracking-[0.14em] nnc-text border border-[#00205B] px-3 py-2 hover:nnc-bg hover:text-white transition-colors"
                  data-testid="lane-grants-link-research"
                >
                  Grant Writing
                </Link>
                <Link
                  to="/services/all-things-grants-management"
                  className="text-xs font-bold uppercase tracking-[0.14em] nnc-text border border-[#00205B] px-3 py-2 hover:nnc-bg hover:text-white transition-colors"
                  data-testid="lane-grants-link-mgmt"
                >
                  Grants Management
                </Link>
              </div>
            </div>

            {/* CONTRACTS LANE */}
            <div className="bg-white p-8 lg:p-10 border-l-4 nnc-gold-border" data-testid="lane-contracts">
              <div className="flex items-center justify-between mb-4">
                <span className="nnc-overline">Lane 02</span>
                <span className="text-xs font-bold uppercase tracking-[0.2em] nnc-text border border-[#00205B] px-3 py-1">
                  Acquisition
                </span>
              </div>
              <h3 className="text-xl lg:text-2xl font-bold nnc-text">
                Government Contracts, Cooperative Agreements &amp; Quotes
              </h3>
              <p className="mt-3 text-sm leading-relaxed nnc-text">
                For-profit firms (and qualifying social enterprises) selling
                goods and services to federal, state, and local government.
                Governed by the FAR, DFARS, GSA Schedule rules, and agency
                supplements. Commercial work in pursuit of contract revenue.
              </p>
              <ul className="mt-5 space-y-2 text-sm nnc-text">
                {[
                  "Capture, RFP/RFQ/RFI, IDIQ, and GSA MAS proposals",
                  "SAM.gov, UEI, CAGE, and NAICS positioning",
                  "8(a), HUBZone, WOSB, SDVOSB certifications",
                  "FAR / DFARS compliance and CPARS management",
                  "Teaming, JVs, and subcontracting plans",
                ].map((x, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="mt-2 inline-block w-2 h-2 nnc-gold-bg shrink-0" />
                    <span>{x}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 flex flex-wrap gap-2">
                <Link
                  to="/services/gov-contracting-capture-proposals"
                  className="text-xs font-bold uppercase tracking-[0.14em] nnc-text border border-[#00205B] px-3 py-2 hover:nnc-bg hover:text-white transition-colors"
                  data-testid="lane-contracts-link-capture"
                >
                  Capture &amp; Proposals
                </Link>
                <Link
                  to="/services/gov-contracting-registrations-certifications"
                  className="text-xs font-bold uppercase tracking-[0.14em] nnc-text border border-[#00205B] px-3 py-2 hover:nnc-bg hover:text-white transition-colors"
                  data-testid="lane-contracts-link-reg"
                >
                  Registrations &amp; Certifications
                </Link>
                <Link
                  to="/services/gov-contracting-administration-compliance"
                  className="text-xs font-bold uppercase tracking-[0.14em] nnc-text border border-[#00205B] px-3 py-2 hover:nnc-bg hover:text-white transition-colors"
                  data-testid="lane-contracts-link-admin"
                >
                  Contract Administration
                </Link>
              </div>
            </div>
          </div>

          <p className="mt-6 text-xs italic text-[#4B5563] max-w-4xl">
            Note: Cooperative agreements are technically a form of federal
            financial assistance under 2 CFR 200, but are often pursued by the
            same firms that pursue FAR-based contracts. We support both
            instruments in either lane based on the client&apos;s role.
          </p>
        </div>
      </section>

      {/* FILTER BAR */}
      <section className="bg-white border-b border-[#E5E7EB] sticky top-20 z-30" data-testid="services-filter-bar">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center gap-3 overflow-x-auto">
          <div className="flex items-center gap-2 shrink-0 nnc-text">
            <Filter className="h-4 w-4 nnc-gold-text" />
            <span className="text-xs font-bold uppercase tracking-[0.2em]">Filter</span>
          </div>
          <div className="flex items-center gap-2">
            {cats.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setActive(c)}
                data-testid={`services-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`text-xs font-bold uppercase tracking-[0.14em] px-4 py-2 border transition-colors whitespace-nowrap ${
                  active === c
                    ? "nnc-bg text-white border-[#00205B]"
                    : "bg-white nnc-text border-[#E5E7EB] hover:border-[#00205B]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* LEDGER */}
      <section className="bg-white" data-testid="services-ledger">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
          <div className="border border-[#00205B] nnc-stagger" data-testid="services-ledger-grid">
            {/* Header row */}
            <div className="hidden md:grid grid-cols-12 nnc-bg text-white">
              <div className="col-span-1 px-4 py-3 text-xs font-bold tracking-[0.2em] uppercase">No.</div>
              <div className="col-span-3 px-4 py-3 text-xs font-bold tracking-[0.2em] uppercase">Practice Area</div>
              <div className="col-span-2 px-4 py-3 text-xs font-bold tracking-[0.2em] uppercase">Category</div>
              <div className="col-span-5 px-4 py-3 text-xs font-bold tracking-[0.2em] uppercase">Description</div>
              <div className="col-span-1 px-4 py-3 text-xs font-bold tracking-[0.2em] uppercase text-right">Open</div>
            </div>

            {filtered.map((s, idx) => (
              <Link
                to={`/services/${s.slug}`}
                key={s.slug}
                className={`group grid grid-cols-1 md:grid-cols-12 items-stretch transition-colors hover:bg-[#FBF2DE] ${
                  idx !== 0 ? "border-t border-[#E5E7EB]" : ""
                }`}
                data-testid={`services-row-${s.slug}`}
              >
                <div className="md:col-span-1 px-4 py-4 md:py-6 nnc-gold-text font-bold tracking-widest text-sm md:border-r md:border-[#E5E7EB] flex md:items-center">
                  {s.number}
                </div>
                <div className="md:col-span-3 px-4 py-2 md:py-6 md:border-r md:border-[#E5E7EB] flex md:items-center">
                  <span className="font-bold nnc-text leading-snug text-base">
                    {s.title}
                  </span>
                </div>
                <div className="md:col-span-2 px-4 py-2 md:py-6 md:border-r md:border-[#E5E7EB] flex md:items-center">
                  <span className="text-xs font-bold uppercase tracking-[0.18em] nnc-text">
                    {s.category}
                  </span>
                </div>
                <div className="md:col-span-5 px-4 py-2 md:py-6 md:border-r md:border-[#E5E7EB] flex md:items-center">
                  <span className="text-sm leading-relaxed nnc-text">{s.summary}</span>
                </div>
                <div className="md:col-span-1 px-4 py-4 md:py-6 flex md:items-center md:justify-end">
                  <ArrowRight className="h-5 w-5 nnc-text group-hover:nnc-gold-text transition-colors" />
                </div>
              </Link>
            ))}
            {filtered.length === 0 && (
              <div className="px-6 py-12 text-center nnc-text" data-testid="services-empty">
                No services in this category.
              </div>
            )}
          </div>
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="nnc-bg" data-testid="services-bottom-cta">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.28em] nnc-gold-text">
              Next Step
            </span>
            <h3 className="mt-3 text-2xl sm:text-3xl font-bold text-white tracking-tight max-w-2xl">
              Don&apos;t see exactly what you need? Our Subject Matter Expert track is built for that.
            </h3>
          </div>
          <Link
            to="/services/subject-matter-expert-consulting"
            className="inline-flex items-center gap-2 bg-[#FCB814] nnc-text font-bold uppercase tracking-[0.06em] text-xs px-6 py-4 border-b-[3px] border-[#00205B] hover:bg-[#fdc73e] transition-colors"
            data-testid="services-bottom-cta-sme"
          >
            SME Consulting <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </main>
  );
};

export default Services;
